// Heures fermées à la réservation en ligne pour une date précise (table time_slot_blocks).
//
// Une ligne = [start_time, end_time) sur block_date (jour de service).
// end_time = 00:00 signifie minuit (fin de journée). Les heures après minuit
// d'un service de nuit restent sur la date de début : une ligne 00:30–01:00
// du jour D bloque une réservation du jour D à 00:30.
//
// GARANTIE : sans ligne, isTimeBlocked renvoie toujours false (aucun changement).
// Pur TypeScript : partagé serveur (Deno) et écrans (Vite), testé par Vitest.

export interface TimeBlockRow {
  block_date: string;
  start_time: string;
  end_time: string;
  /** true = fermeture « à partir de » : la réservation ne peut pas non plus déborder dedans. */
  blocks_overlap?: boolean | null;
}

function toMin(time: string): number {
  const [h, m] = time.substring(0, 5).split(":").map(Number);
  return h * 60 + m;
}

function blockEnd(row: TimeBlockRow): number {
  const e = toMin(row.end_time);
  return e === 0 ? 1440 : e;
}

/**
 * L'heure `time` du jour `date` est-elle fermée ?
 * - ligne classique : seule l'heure de début compte (comportement historique) ;
 * - ligne blocks_overlap : refus aussi si [time, time + durationMinutes) déborde dans la tranche.
 * Sans durationMinutes, seule l'heure de début est testée pour toutes les lignes.
 */
export function isTimeBlocked(
  date: string,
  time: string,
  blocks: TimeBlockRow[] | null | undefined,
  durationMinutes?: number | null,
): boolean {
  if (!blocks || blocks.length === 0) return false;
  const t = toMin(time);
  const dur = durationMinutes && durationMinutes > 0 ? durationMinutes : 0;
  return blocks.some(b => {
    if (b.block_date !== date) return false;
    const s = toMin(b.start_time);
    let e = blockEnd(b);
    let tt = t;
    // « À partir de … » jusqu'à minuit : couvre aussi la fin d'un service de nuit (jusqu'à 06:00)
    if (b.blocks_overlap && e === 1440) {
      e = 1440 + 360;
      if (t < 360 && t < s) tt = t + 1440;
    }
    if (tt >= s && tt < e) return true;
    if (tt !== t) return false;
    return !!b.blocks_overlap && dur > 0 && t < s && t + dur > s;
  });
}

/** Durée d'une réservation commençant à `time` : override du créneau qui la contient, sinon défaut. */
export function durationForTime(
  time: string,
  ranges: { start: string; end: string; durationOverrideMinutes?: number | null }[],
  defaultMinutes: number,
): number {
  const t = toMin(time);
  for (const r of ranges) {
    const s = toMin(r.start);
    let e = toMin(r.end);
    if (e <= s) e += 1440;
    const tt = t < s ? t + 1440 : t;
    if (tt >= s && tt < e) return r.durationOverrideMinutes ?? defaultMinutes;
  }
  return defaultMinutes;
}

/** Retire les heures fermées d'une liste (grilles client). Liste identique si aucune ligne. */
export function filterBlockedTimes<T extends string>(
  date: string,
  times: T[],
  blocks: TimeBlockRow[] | null | undefined,
  durationFor?: (time: T) => number | null | undefined,
): T[] {
  if (!blocks || blocks.length === 0) return times;
  return times.filter(t => !isTimeBlocked(date, t, blocks, durationFor ? durationFor(t) : undefined));
}

/** Plage stockée quand l'équipe ferme l'heure `time` avec l'intervalle courant. */
export function blockRangeFor(time: string, intervalMinutes: number): { start_time: string; end_time: string } {
  const start = toMin(time);
  const end = (start + intervalMinutes) % 1440;
  const fmt = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  return { start_time: fmt(start), end_time: fmt(end) };
}

/** Durée d'un créneau « HH:MM - HH:MM » (fin <= début = après minuit). */
export function slotLabelMinutes(label: string): number {
  const [a, b] = label.split(" - ");
  if (!a || !b) return 0;
  const s = toMin(a);
  let e = toMin(b);
  if (e <= s) e += 1440;
  return e - s;
}
